import { Button, MenuItem, TextField } from '@material-ui/core'
import { ChangeEvent, useEffect, useState } from 'react'
import { TFunction } from 'i18next'
import { UpdateStatus } from '../../../../actions/types'
import { MuiClasses } from '../../../../types'
import { ApartmentStatus, Status } from '../../../../types/apartments'

/**
 * Admin-only dropdown to set an apartment's status directly. Replaces the
 * "cancel listing" button in the overview. `rented` and `rentContractWanted`
 * are intentionally excluded — they have dedicated flows (win-back rent billing
 * and application chaining) that a raw status set would bypass.
 */
const STATUS_OPTIONS: Status[] = [
  ApartmentStatus.created,
  ApartmentStatus.confirmed,
  ApartmentStatus.marketing,
  ApartmentStatus.followUp,
  ApartmentStatus.cancelled
]

interface AdminApartmentStatusDropdownProps {
  currentStatus: Status
  updateStatus: UpdateStatus
  t: TFunction
  classes: MuiClasses
}

const AdminApartmentStatusDropdown = ({
  currentStatus,
  updateStatus,
  t,
  classes
}: AdminApartmentStatusDropdownProps) => {
  const [selectedStatus, setSelectedStatus] = useState<Status>(currentStatus)

  // Keep the selection in sync when the apartment's status changes (e.g. after
  // a successful update), so the dropdown reflects the current status.
  useEffect(() => {
    setSelectedStatus(currentStatus)
  }, [currentStatus])

  // Lock raw status sets out of rented/rentContractWanted (dedicated flow only).
  const locked =
    currentStatus === ApartmentStatus.rented ||
    currentStatus === ApartmentStatus.rentContractWanted

  const canSubmit =
    !locked &&
    STATUS_OPTIONS.includes(selectedStatus) &&
    selectedStatus !== currentStatus

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'stretch',
        verticalAlign: 'middle',
        margin: '0 8px',
        height: 37
      }}
    >
      <TextField
        select
        size='small'
        variant='outlined'
        disabled={locked}
        title={locked ? t('adminApartments.changeStatus.lockedHint') : undefined}
        label={t('adminApartments.changeStatus.setStatus')}
        value={STATUS_OPTIONS.includes(selectedStatus) ? selectedStatus : ''}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          setSelectedStatus(e.target.value as Status)
        }
        style={{ minWidth: 170, backgroundColor: '#fff' }}
        InputProps={{ style: { height: 37 } }}
      >
        {STATUS_OPTIONS.map((statusOption) => (
          <MenuItem key={statusOption} value={statusOption}>
            {t(`adminApartments.statuses.${statusOption}`)}
          </MenuItem>
        ))}
      </TextField>
      <Button
        size='small'
        className={classes.startMarketing}
        disabled={!canSubmit}
        onClick={() => updateStatus(selectedStatus)}
        style={{ marginLeft: 6, minWidth: 56 }}
      >
        OK
      </Button>
    </span>
  )
}

export default AdminApartmentStatusDropdown
